import util from 'util';
import { CONSTANT } from '@const';
import {
	ClientType,
	DeviceFilter,
	DeviceSummary,
	DeviceType,
	FaultyDeviceRequestBody,
	LocalDeviceFilter,
	Page,
	PageFilter,
} from '@types';
import {
	convertMinutesToMs,
	filterDevices,
	removeDuplicates,
	sortAscDevicesByDisplayNameAndReference,
	sortObjectsByProperties,
} from '@utils';

import { ApiParams, ApiService } from '../api/ApiService';

export class DeviceService extends ApiService {
	static async getDevices(
		clientId: string,
		deviceFilter?: DeviceFilter,
		localFilters?: LocalDeviceFilter,
	): Promise<DeviceSummary[]> {
		const allItemsPageFilter: PageFilter = { limit: 1000, offSet: 0 };
		const params: ApiParams = {
			method: 'GET',
			url: util.format(`${CONSTANT.DEVICES_API_URL}`, clientId),
			queryParams: { ...deviceFilter, ...allItemsPageFilter },
			staleTimeInMs: convertMinutesToMs(5),
		};

		return await this.executeApiRequest(params).then((data: Page<DeviceSummary>) => {
			const devicesWithClientId = this.setClientIdOnDevices(clientId, data.items);
			return localFilters ? filterDevices(devicesWithClientId, localFilters) : devicesWithClientId;
		});
	}

	static async getSortedDevices(
		clientId: string,
		deviceFilter?: DeviceFilter,
		localFilters?: LocalDeviceFilter,
	): Promise<DeviceSummary[]> {
		return await this.getDevices(clientId, deviceFilter, localFilters).then(data => {
			return sortAscDevicesByDisplayNameAndReference(data);
		});
	}

	static async getAllDevices(
		clients: ClientType[],
		deviceFilter?: DeviceFilter,
		localFilters?: LocalDeviceFilter,
	) {
		let allDevices: DeviceSummary[] = [];
		for (const client of clients) {
			const clientDevices = await this.getDevices(client.id, deviceFilter, localFilters);
			allDevices = allDevices.concat(clientDevices);
		}
		return removeDuplicates(allDevices);
	}

	static async getDeviceDetails(clientId: string, deviceId: string): Promise<DeviceType> {
		const params: ApiParams = {
			method: 'GET',
			url: util.format(`${CONSTANT.DEVICE_API_URL}`, clientId, deviceId),
		};
		return await this.executeApiRequest(params).then((data: DeviceType) => {
			data.connectedDevices = sortObjectsByProperties(data.connectedDevices ?? [], [
				'type',
				'displayName',
				'reference',
			]);
			return data;
		});
	}

	static async updateDevice(clientId: string, device: Partial<DeviceType>): Promise<DeviceType> {
		const params: ApiParams = {
			method: 'PUT',
			url: util.format(CONSTANT.DEVICE_API_URL, clientId, device.id),
			data: device,
		};
		return await this.executeApiRequest(params);
	}

	static async reportDeviceAsFaulty(
		clientId: string,
		deviceId: string,
		payload: FaultyDeviceRequestBody,
	): Promise<void> {
		const params: ApiParams = {
			method: 'POST',
			url: util.format(CONSTANT.DEVICE_FAULTY_API_URL, clientId, deviceId),
			data: payload,
		};
		return await this.executeApiRequest(params);
	}

	private static setClientIdOnDevices(clientId: string, devices: DeviceSummary[]): DeviceSummary[] {
		return devices.map(device => ({ ...device, clientId: clientId }));
	}
}
